import { useState } from "react"
import { get, post } from "@/services/api"
import { API_ENDPOINTS } from "@/config/api"
import { useAuthStore } from "@/stores"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from "@/components/ui"
import {
  Plus,
  X,
  ArrowRightLeft,
  RefreshCw,
  DollarSign,
} from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

/**
 * Floating Action Button con 3 acciones rapidas:
 * 1. Transferencia wallet2 -> wallet1
 * 2. Actualizar Wallets
 * 3. Solicitar Comisiones
 *
 * Visible para B y C (operan con wallets reales). Oculto para Admin y A
 * (A maneja credito ilimitado sin wallets). Para C solo "Actualizar Wallets"
 * (no tiene wallet2: sin transferir W2->W1 ni solicitar comisiones).
 */
export function FloatingActionButton() {
  const { user } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)

  // Modals
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showSolicitarModal, setShowSolicitarModal] = useState(false)

  // Form state
  const [transferAmount, setTransferAmount] = useState("")
  const [solicitarAmount, setSolicitarAmount] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)

  // Wallets actualizados (se cargan al abrir modal)
  const [wallet1, setWallet1] = useState<number>(0)
  const [wallet2, setWallet2] = useState<number>(0)
  const [cuentaBancaria, setCuentaBancaria] = useState("")

  if (!user) return null
  // Admin no opera wallets. A maneja credito ilimitado (sin wallets) -> el FAB no le sirve.
  if (user.tipo_usuario === "Admin") return null
  if (user.tipo_usuario === "A") return null

  // Acciones del FAB por tipo:
  // - B: tiene wallet1 (tiempo aire) + wallet2 (comisiones) -> transferir
  //   W2->W1, actualizar y solicitar pago.
  // - M (override): tiene comisiones (wallet2) pero NO tiempo aire (wallet1)
  //   -> solo solicitar pago (no hay W1 a dónde transferir).
  // - C y R: NO tienen wallet2/comisiones -> solo actualizar su saldo.
  const esC = user.tipo_usuario === "C"
  const esR = user.tipo_usuario === "R"
  const esM = user.tipo_usuario === "M"
  const canTransfer = user.tipo_usuario === "B" // único con W1 y W2
  const canRefresh = true
  const canSolicitar = esM || user.tipo_usuario === "B" // tienen wallet2/comisiones

  const formatMoney = (amount: number) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(amount)

  const loadWallets = async () => {
    try {
      const res = await get<{ success: boolean; data?: { wallet1?: number; wallet2?: number; cuenta_bancaria?: string } }>(
        API_ENDPOINTS.WALLETS.BALANCES
      )
      if (res.success && res.data) {
        setWallet1(res.data.wallet1 ?? user.wallet1 ?? 0)
        setWallet2(res.data.wallet2 ?? user.wallet2 ?? 0)
        setCuentaBancaria(res.data.cuenta_bancaria ?? user.cuenta_bancaria ?? "")
      } else {
        setWallet1(user.wallet1 ?? 0)
        setWallet2(user.wallet2 ?? 0)
        setCuentaBancaria(user.cuenta_bancaria ?? "")
      }
    } catch {
      setWallet1(user.wallet1 ?? 0)
      setWallet2(user.wallet2 ?? 0)
      setCuentaBancaria(user.cuenta_bancaria ?? "")
    }
  }

  const handleOpenTransfer = async () => {
    setIsOpen(false)
    setTransferAmount("")
    await loadWallets()
    setShowTransferModal(true)
  }

  const handleOpenSolicitar = async () => {
    setIsOpen(false)
    setSolicitarAmount("")
    await loadWallets()
    setShowSolicitarModal(true)
  }

  const handleRefreshWallets = async () => {
    setIsOpen(false)
    try {
      const res = await get<{ success: boolean; data?: { wallet1?: number; wallet2?: number } }>(
        API_ENDPOINTS.WALLETS.BALANCES
      )
      if (res.success && res.data) {
        // Actualizar auth store con los nuevos saldos
        const authState = useAuthStore.getState()
        if (authState.user) {
          useAuthStore.setState({
            user: {
              ...authState.user,
              wallet1: res.data.wallet1 ?? authState.user.wallet1,
              wallet2: res.data.wallet2 ?? authState.user.wallet2,
            },
          })
        }
        toast.success("Saldos actualizados")
      }
    } catch {
      toast.error("Error al actualizar saldos")
    }
  }

  const handleTransfer = async () => {
    const amount = parseFloat(transferAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error("Ingresa un monto válido")
      return
    }
    if (amount > wallet2) {
      toast.error("El monto excede el saldo del Wallet 2")
      return
    }
    try {
      setIsProcessing(true)
      // El endpoint exige wallet_origen/wallet_destino (ver schema
      // TransferenciaWalletRequest). El FAB siempre transfiere W2->W1
      // (comisiones -> tiempo aire), igual que el modal de Saldos.
      const res = await post<{ success: boolean; message?: string }>(
        API_ENDPOINTS.WALLETS.TRANSFER_BETWEEN,
        { monto: transferAmount, wallet_origen: 2, wallet_destino: 1 }
      )
      if (res.success) {
        toast.success("Transferencia realizada correctamente")
        setShowTransferModal(false)
        // Refresh wallets in auth store
        handleRefreshWallets()
      } else {
        toast.error(res.message || "Error en la transferencia")
      }
    } catch {
      toast.error("Error al realizar la transferencia")
    } finally {
      setIsProcessing(false)
    }
  }

  const handleSolicitar = async () => {
    const amount = parseFloat(solicitarAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error("Ingresa un monto válido")
      return
    }
    if (amount > wallet2) {
      toast.error("El monto excede el saldo del Wallet 2")
      return
    }
    try {
      setIsProcessing(true)
      const res = await post<{ success: boolean; message?: string }>(
        API_ENDPOINTS.COMMISSIONS.REQUEST_PAYMENT,
        { monto: solicitarAmount }
      )
      if (res.success) {
        toast.success("Solicitud de comisiones enviada")
        setShowSolicitarModal(false)
      } else {
        toast.error(res.message || "Error al solicitar comisiones")
      }
    } catch {
      toast.error("Error al solicitar comisiones")
    } finally {
      setIsProcessing(false)
    }
  }

  const actions = [
    ...(canTransfer
      ? [
          {
            id: "transfer",
            label: "Transferir W2→W1",
            icon: ArrowRightLeft,
            color: "bg-blue-600 hover:bg-blue-700",
            onClick: handleOpenTransfer,
          },
        ]
      : []),
    ...(canRefresh
      ? [
          {
            id: "refresh",
            // C/R tienen un solo saldo (tiempo aire); M solo comisiones; B ambos.
            label: esC || esR ? "Actualizar saldo" : esM ? "Actualizar comisiones" : "Actualizar Wallets",
            icon: RefreshCw,
            color: "bg-emerald-600 hover:bg-emerald-700",
            onClick: handleRefreshWallets,
          },
        ]
      : []),
    ...(canSolicitar
      ? [
          {
            id: "solicitar",
            label: "Solicitar Comisiones",
            icon: DollarSign,
            color: "bg-amber-600 hover:bg-amber-700",
            onClick: handleOpenSolicitar,
          },
        ]
      : []),
  ]

  return (
    <>
      {/* FAB Container */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col-reverse items-end gap-3">
        {/* Action buttons (shown when open) */}
        {isOpen &&
          actions.map((action, index) => (
            <div
              key={action.id}
              className="flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2"
              style={{ animationDelay: `${index * 50}ms`, animationFillMode: "both" }}
            >
              <span className="bg-slate-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                {action.label}
              </span>
              <button
                onClick={action.onClick}
                className={cn(
                  "h-12 w-12 rounded-full text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110",
                  action.color
                )}
              >
                <action.icon className="h-5 w-5" />
              </button>
            </div>
          ))}

        {/* Main FAB button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "h-14 w-14 rounded-full text-white shadow-xl flex items-center justify-center transition-all duration-200",
            isOpen
              ? "bg-slate-700 hover:bg-slate-800 rotate-45"
              : "bg-rose-600 hover:bg-rose-700"
          )}
        >
          {isOpen ? <X className="h-6 w-6 -rotate-45" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>

      {/* Backdrop when FAB is open */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Transfer Modal: wallet2 -> wallet1 */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5" />
                Transferencia Wallet 2 → Wallet 1
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowTransferModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Wallet 1 (Tiempo Aire)</p>
                  <p className="text-lg font-bold text-emerald-600">{formatMoney(wallet1)}</p>
                </div>
                <div className="rounded-lg border p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">Wallet 2 (Comisiones)</p>
                  <p className="text-lg font-bold text-blue-600">{formatMoney(wallet2)}</p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
                Solo se puede transferir el dinero del Wallet 2 al Wallet 1.
              </p>

              <div>
                <Label htmlFor="transfer-amount">Monto a transferir *</Label>
                <Input
                  id="transfer-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={wallet2}
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Máximo: {formatMoney(wallet2)}
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowTransferModal(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleTransfer}
                  disabled={!transferAmount || isProcessing}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {isProcessing ? "Transfiriendo..." : "Transferir"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Solicitar Comisiones Modal */}
      {showSolicitarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Solicitar Comisiones
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowSolicitarModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Wallet 2 (Comisiones)</p>
                <p className="text-lg font-bold text-blue-600">{formatMoney(wallet2)}</p>
              </div>

              <div>
                <Label htmlFor="solicitar-cuenta">Cuenta Bancaria</Label>
                <Input
                  id="solicitar-cuenta"
                  value={cuentaBancaria || "Sin cuenta registrada"}
                  readOnly
                  className="bg-muted"
                />
              </div>

              <div>
                <Label htmlFor="solicitar-amount">Monto a solicitar *</Label>
                <Input
                  id="solicitar-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={wallet2}
                  value={solicitarAmount}
                  onChange={(e) => setSolicitarAmount(e.target.value)}
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Máximo: {formatMoney(wallet2)}
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowSolicitarModal(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSolicitar}
                  disabled={!solicitarAmount || isProcessing}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {isProcessing ? "Solicitando..." : "Solicitar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}
